import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Plus,
  Play,
  List,
  UtensilsCrossed,
  Camera,
  Video,
  Droplets,
} from "lucide-react";
import AuthButton from '@/components/AuthButton';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';


interface TabItem {
  name: string;
  path: string;
  icon: React.ReactNode;
  dataTour?: string;
}

const tabs: TabItem[] = [
  {
    name: "Boards",
    path: "/boards",
    icon: <List className="w-5 h-5" />,
  },
  {
    name: "Drips",
    path: "/drips",
    icon: <Droplets className="w-5 h-5" />,
    dataTour: "drips-tab",
  },
  // Placeholder for center FAB
  {
    name: "",
    path: "",
    icon: null,
  },
  {
    name: "Feed",
    path: "/feed",
    icon: <Play className="w-5 h-5" />,
    dataTour: "feed-tab",
  },
];

interface BottomTabBarProps {
  showFab?: boolean;
  isGuest?: boolean;
  isImmersive?: boolean;
  onSubmitDish?: () => void;
  onPostSnap?: () => void;
  onPostDrool?: () => void;
}

export const BottomTabBar: React.FC<BottomTabBarProps> = ({
  showFab = false,
  isGuest = false,
  isImmersive = false,
  onSubmitDish,
  onPostSnap,
  onPostDrool,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);

  const handleFabClick = () => {
    if (isGuest) {
      navigate('/login');
      return;
    }
    setIsPopoverOpen(true);
  };

  const handleOptionClick = (callback?: () => void) => {
    setIsPopoverOpen(false);
    callback?.();
  };

  return (
    <nav
      className={`fixed bottom-0 left-0 right-0 z-50 ${isImmersive ? 'bg-black/40 backdrop-blur-md' : 'bg-background/80 backdrop-blur-md'}`}
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className={`flex items-center justify-around px-3 py-2.5 border-t ${
        isImmersive
          ? 'border-white/10'
          : 'border-border'
      }`}>
        {tabs.map((tab, index) => {
          // Center position - render FAB
          if (index === 2) {
            if (!showFab) {
              return <div key="fab-placeholder" className="w-12 h-12" />;
            }

            return (
              <Popover key="fab" open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                <PopoverTrigger asChild>
                  <button
                    data-tour="fab-create"
                    className="h-12 w-12 rounded-full shadow-md hover:shadow-lg transition-transform duration-300 hover:scale-110 flex items-center justify-center mx-1"
                    style={{ backgroundColor: '#cfef00' }}
                    aria-label="Create new content"
                    onClick={(e) => {
                      if (isGuest) {
                        e.preventDefault();
                        handleFabClick();
                      }
                    }}
                  >
                    <Plus className="h-6 w-6 text-black" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-52 p-3 bg-background/95 backdrop-blur-md border-border/50 rounded-2xl shadow-xl"
                  align="center"
                  side="top"
                  sideOffset={16}
                >
                  <div className="flex flex-col gap-1.5">
                    <button
                      onClick={() => handleOptionClick(onSubmitDish)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-accent transition-colors text-left"
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#cfef00]/20">
                        <UtensilsCrossed className="h-4 w-4 text-[#8fb800]" />
                      </div>
                      <span className="text-sm font-medium text-foreground">Post Dish</span>
                    </button>
                    <button
                      onClick={() => handleOptionClick(onPostSnap)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-accent transition-colors text-left"
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-sky-500/15">
                        <Camera className="h-4 w-4 text-sky-500" />
                      </div>
                      <span className="text-sm font-medium text-foreground">Post Snap</span>
                    </button>
                    <button
                      onClick={() => handleOptionClick(onPostDrool)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-accent transition-colors text-left"
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-500/15">
                        <Video className="h-4 w-4 text-violet-500" />
                      </div>
                      <span className="text-sm font-medium text-foreground">Post Drool</span>
                    </button>
                  </div>
                </PopoverContent>
              </Popover>
            );
          }

          const isActive = location.pathname.startsWith(tab.path);

          return (
            <button
              key={tab.name}
              data-tour={tab.dataTour}
              className={`h-12 flex flex-col items-center justify-center px-3 gap-0.5 focus:outline-none transition-colors ${
                isActive
                  ? isImmersive ? "text-white font-semibold" : "text-forest font-semibold"
                  : isImmersive ? "text-white/60 font-medium" : "text-muted-foreground font-medium"
              }`}
              onClick={() => navigate(tab.path)}
            >
              {tab.icon}
              <span className="text-[10px]">{tab.name}</span>
            </button>
          );
        })}

        {/* Profile */}
        <div className={`h-12 flex flex-col items-center justify-center px-3 gap-0.5 ${
          isImmersive ? 'text-white/60' : 'text-muted-foreground'
        }`}>
          <AuthButton className="h-5 w-5 ring-2 ring-gray-200 hover:ring-gray-300 transition-all duration-200 cursor-pointer" />
          <span className="text-[10px] font-medium">Profile</span>
        </div>
      </div>
    </nav>
  );
};
